import React from "react";
import Form from "@rjsf/core";
import type {
  RegistryFieldsType,
  RegistryWidgetsType,
  TemplatesType,
} from "@rjsf/utils";
import { spacing } from "../../../../../../stitches.config";
import { formatDateString } from "../../../../../../utils/dates";
import { Header } from "../../../../../Common/__styles__/FullPageFormLayout";
import { EmptyState } from "../../../../../Common/EmptyState";
import { Title } from "../../../../../Common/Typography";
import { DescriptionContainer } from "../../../../../Submissions/__styles__/CreateSubmission";
import { SubmissionFormContext } from "../../../../../Submissions/Form";
import {
  DescriptionFieldTemplate,
  TitleFieldTemplate,
} from "../../../../../Submissions/Templates";
import { ColumnWrapper } from "../__styles__/SubmissionsBuilder";
import { useBuilderFormContext } from "../form";
import { getSchemaHasAtLeastOneItem } from "../schemaUtils";
import {
  DocumentUploadFieldPreview,
  MultiSelectFieldPreview,
  NumberFieldPreview,
  NumberWithUnitsFieldPreview,
  PropertyMarketValueFieldPreview,
  TagFieldPreview,
  UniqueIdFieldPreview,
  UserFieldPreview,
} from "./FieldPreviews/Fields/FieldPreviews";
import { ObjectFieldTemplatePreview } from "./FieldPreviews/Templates/ObjectFieldTemplatePreview";
import {
  CheckboxWidgetPreview,
  DamageDegreeWidgetPreview,
  DateWidgetPreview,
  SelectWidgetPreview,
  TextareaWidgetPreview,
  TextWidgetPreview,
} from "./FieldPreviews/Widgets/WidgetPreviews";

export function MainColumn() {
  const { watch } = useBuilderFormContext();

  const {
    name,
    description,
    formStructure: { schema },
  } = watch();

  return (
    <SubmissionFormContext.Provider
      value={{
        editing: false,
        documentUploads: null,
        defaultValues: {
          units: {
            feet: "feet",
            inches: "inches",
            hours: "hours",
            minutes: "minutes",
            days: "days",
          } as const,
          boolean: { true: true, false: false } as const,
          date: {
            today: formatDateString({
              dateString: new Date().toDateString(),
              format: "YYYY-MM-DD",
            }),
          },
          property: {
            improvementValue: 1000,
            sisd: {
              adjustmentRatio: 0.5,
            },
          },
        },
        regenerateAIFields: null,
        loadingFormPaths: {},
      }}
    >
      <ColumnWrapper role="main">
        <div style={{ maxWidth: "564px", width: "100%", margin: "0 auto" }}>
          <Header css={{ marginBottom: spacing.l }}>
            <Title
              style={{ marginLeft: spacing.m.toString() }}
              size="default"
              type="semiBold"
            >
              {name}
            </Title>
            {description && <DescriptionContainer markdown={description} />}
          </Header>
          {getSchemaHasAtLeastOneItem(schema) ?
            <FormWrapper />
          : <EmptyForm />}
        </div>
      </ColumnWrapper>
    </SubmissionFormContext.Provider>
  );
}

function EmptyForm() {
  return (
    <div style={{ marginTop: "128px" }}>
      <EmptyState
        icon="add-file"
        title="No fields added to your form"
        body="Add a field to get started."
      />
    </div>
  );
}

const validatorShim = {
  isValid: () => true,
  toErrorList: () => [],
  rawValidation: () => ({}),
  validateFormData: () => ({
    errors: [],
    errorSchema: {},
  }),
};

const builderFields: RegistryFieldsType = {
  DocumentUploader: DocumentUploadFieldPreview,
  // lowercase to override the default [Select] widget preview
  multiSelect: MultiSelectFieldPreview,
  NumberField: NumberFieldPreview,
  NumberWithUnits: NumberWithUnitsFieldPreview,
  PropertyMarketValue: PropertyMarketValueFieldPreview,
  Tags: TagFieldPreview,
  UniqueId: UniqueIdFieldPreview,
  User: UserFieldPreview,
};

const builderWidgets: RegistryWidgetsType = {
  CheckboxWidget: CheckboxWidgetPreview,
  DamageDegree: DamageDegreeWidgetPreview,
  DateWidget: DateWidgetPreview,
  TextareaWidget: TextareaWidgetPreview,
  TextWidget: TextWidgetPreview,
  SelectWidget: SelectWidgetPreview,
};

const builderTemplates: Partial<TemplatesType> = {
  TitleFieldTemplate: TitleFieldTemplate,
  DescriptionFieldTemplate: DescriptionFieldTemplate,
  ObjectFieldTemplate: ObjectFieldTemplatePreview,
};

function FormWrapper() {
  const { watch } = useBuilderFormContext();
  const formStructure = watch("formStructure");

  return (
    <Form
      schema={formStructure.schema}
      uiSchema={{
        ...formStructure.uiSchema,
        "ui:submitButtonOptions": { norender: true },
      }}
      widgets={builderWidgets}
      fields={builderFields}
      templates={builderTemplates}
      noHtml5Validate={true}
      showErrorList={false}
      validator={validatorShim}
    />
  );
}
